import { EditIcon } from '@persona-id/design-tokens/icons';
import { ChangeEvent, useMemo, VFC } from 'react';
import styled from 'styled-components';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import PlatformSchemaEditor from 'dashboard/components/PlatformSchemaEditor';
import ReactSelect from 'dashboard/components/ReactSelect';
import JsonSchemaPreview from 'dashboard/components/WorkflowControlPanel/JsonSchemaPreview';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import ParamsHttpRequestHeaders from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsHttpRequestHeaders';
import RetryIntervals from 'dashboard/components/WorkflowControlPanel/StepConfigAction/RetryIntervals';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionHttpRequestV1 } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { FieldSchemaJson, FieldSchemaType, JsonSchemaStringifiableTypes } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchemaTypes } from 'lib/json-schema';

const Row = styled.div`
  display: flex;
  align-items: center;
`;

interface ParamsHttpRequestV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionHttpRequestV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionHttpRequestV1['params']) => void;
}

export const defaultSchemaForParams: FieldSchemaJson = {
  type: FieldSchemaType.Json,
  key: 'result',
  label: 'Result',
  config: {
    'json-schema': '{}',
  },
};

const SUPPORTED_REQUEST_METHODS = ['DELETE', 'GET', 'PATCH', 'POST', 'PUT'];
const REQUEST_BODY_ENABLED_METHODS = ['PATCH', 'POST', 'PUT'];

const ParamsHttpRequestV1: VFC<ParamsHttpRequestV1Props> = ({
  step,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const [isSchemaEditorOpen, { disable: closeSchemaEditor, toggle: toggleSchemaEditor }] =
    useToggle(false);
  const methodOptions = SUPPORTED_REQUEST_METHODS.map((m) => ({
    label: m,
    value: m as WorkflowActionHttpRequestV1['params']['method'],
  }));

  const updateHttpRequestParams = <Key extends keyof WorkflowActionHttpRequestV1['params']>(
    key: Key,
    value: WorkflowActionHttpRequestV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const updateHttpRequestType = (value: WorkflowActionHttpRequestV1['params']['method']) => {
    const newParams: WorkflowActionHttpRequestV1['params'] = {
      ...params,
      method: value,
    };

    if (value === 'POST') {
      newParams['return-schema'] = null;
    }

    updateActionParams(newParams);
  };

  const updateRetryIntervals = (retryIntervals: number[]) => {
    updateActionParams({
      ...params,
      ['retry-intervals']: retryIntervals,
    });
  };

  const onChangeTimeout = (event: ChangeEvent<HTMLInputElement>) => {
    const timeout = parseInt(event.target.value);

    updateActionParams({
      ...params,
      timeout: isNaN(timeout) ? null : timeout,
    });
  };

  const jsonSchemaField = params['return-schema'] || defaultSchemaForParams;
  const updateReturnSchema = (newSchema: string) => {
    updateActionParams({
      ...params,
      'return-schema': {
        ...jsonSchemaField,
        config: {
          'json-schema': newSchema,
        },
      },
    });
  };

  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionHttpRequestV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='URL' htmlFor='params-http-request__url' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['url']}
        updateValue={(value: string) => {
          updateHttpRequestParams('url', value);
        }}
        editable={editable}
        allowedTypes={[JsonSchemaTypes.String]}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        placeholder='https://...'
        id='params-http-request__url'
        hasError={hasErrorFor('url')}
      />
      {renderErrorMessageFor('url')}
      <Spacer height={2} />
      <ParamLabel labelText='Request Type' htmlFor='params-http-request__request-type-input' />
      <ReactSelect
        value={params['method']}
        options={methodOptions}
        onChange={updateHttpRequestType}
        id='params-http-request__request-type'
        disabled={!editable}
        hasError={hasErrorFor('method')}
      />
      {renderErrorMessageFor('method')}
      {isSchemaEditorOpen && (
        <PlatformSchemaEditor
          panelTitle='Response Schema'
          jsonSchema={JSON.parse(jsonSchemaField.config['json-schema'])}
          templateOptionsByObjectKey={{}}
          includePersonaObjects={false}
          onClosePanel={closeSchemaEditor}
          updateJsonSchema={(newSchema) => {
            updateReturnSchema(JSON.stringify(newSchema));
          }}
        />
      )}
      <Spacer height={2} />
      <ParamsHttpRequestHeaders
        jsonSchema={jsonSchema}
        allowedTypes={[JsonSchemaTypes.String]}
        tokensToDisplayValue={tokensToDisplayValue}
        editable={editable}
        headers={params['headers-v2'] || []}
        updateHeaders={(newHeaders) => {
          updateHttpRequestParams('headers-v2', newHeaders);
        }}
      />
      {renderErrorMessageFor('headers-v2')}
      {REQUEST_BODY_ENABLED_METHODS.includes(params['method']) && (
        <>
          <Spacer height={2} />
          <Row>
            <ParamLabel htmlFor='params-http-request__body' labelText='Body (optional)' />
            <HelpIcon>
              Input any JSON data you want to send with this {params['method']} request
            </HelpIcon>
          </Row>
          <HandlebarFormControl
            size='md'
            id='params-http-request__body'
            type='code'
            value={params.body}
            editable={editable}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            allowedTypes={[JsonSchemaTypes.String]}
            updateValue={(newBody) => {
              updateHttpRequestParams('body', newBody);
            }}
          />
          {renderErrorMessageFor('body')}
        </>
      )}
      <Spacer height={2} />
      <ParamLabel
        labelText={'Response Schema' + (params['method'] !== 'GET' ? ' (optional)' : '')}
        htmlFor='params-http-request__schema'
        buttons={[
          <Button
            key='edit'
            width='icon'
            variant='basic'
            disabled={!editable}
            onClick={toggleSchemaEditor}
            id='params-http-request__schema'
          >
            <EditIcon />
          </Button>,
        ]}
      />
      <Spacer height={1} />
      <JsonSchemaPreview
        id='params-http-request__return-schema-preview'
        jsonSchema={JSON.parse(jsonSchemaField.config['json-schema'])?.['properties'] ?? {}}
        requiredFields={JSON.parse(jsonSchemaField.config['json-schema'])?.['required'] ?? []}
        onClick={toggleSchemaEditor}
      />
      {renderErrorMessageFor('return-schema')}
      <Spacer height={4} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        <ParamLabel labelText='Persona Secret' htmlFor='params-http-request__persona-secret' />
        <Paragraph> A secret value used to sign outgoing requests </Paragraph>
        <HandlebarFormControl
          size='md'
          type='input'
          value={params['persona-secret'] || ''}
          updateValue={(value: string) => {
            updateHttpRequestParams('persona-secret', value);
          }}
          editable={editable}
          allowedTypes={JsonSchemaStringifiableTypes}
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          placeholder='Enter secret value...'
          id='params-http-request__persona-secret'
        />
        <Spacer height={2} />
        <RetryIntervals
          editable={editable}
          retryIntervals={params['retry-intervals'] ?? []}
          updateRetryIntervals={updateRetryIntervals}
        />
        <>
          <Spacer height={2} />
          <ParamLabel labelText='Request Timeout' htmlFor='params-http-request__timeout' />
          <Paragraph>
            {' '}
            Number of seconds to wait for the request to complete before erroring
          </Paragraph>
          <Spacer height={1} />
          <Input
            id='params-http-request__timeout'
            type='number'
            value={params['timeout'] ?? ''}
            hasError={hasErrorFor('timeout')}
            aria-invalid={hasErrorFor('timeout')}
            onChange={onChangeTimeout}
            disabled={!editable}
          />
          {renderErrorMessageFor('timeout')}
        </>
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsHttpRequestV1;
